import type { ECharts } from "echarts/core";
import { useEffect, useState } from "react";

/** Reads chart pixels after each echarts render, since the panel pushes options after the overlays render. An equal result keeps the previous one. */
export function useChartProjection<T>(
    chart: ECharts | null,
    project: (chart: ECharts) => T,
    isEqual: (previous: T, next: T) => boolean = Object.is,
): T | null {
    const [value, setValue] = useState<T | null>(null);

    useEffect(() => {
        if (!chart) {
            setValue(null);
            return undefined;
        }
        const measure = () => {
            if (chart.isDisposed()) return;
            const next = project(chart);
            setValue((previous) => (previous !== null && isEqual(previous, next) ? previous : next));
        };
        measure();
        chart.on("finished", measure);
        return () => {
            if (!chart.isDisposed()) chart.off("finished", measure);
        };
    }, [chart, project, isEqual]);

    return value;
}
